/**
 * Ability effect registry (Phase 2) — the pluggable effect system.
 *
 * The engine core (index.js) knows only this generic interface; it never
 * branches on character names. Characters reference abilities by id in the
 * data table (characters.js), so rebalancing = editing numbers there.
 *
 * Each ability definition:
 *   movement   : true if the ability moves the pawn. Movement abilities are
 *                blocked while swamped, and their landing goes through the
 *                shared landing pipeline (win check, swamp/ice effects).
 *   pathEffect : 'wallCrossing' | null. 'wallCrossing' means the remaining
 *                uses count as wall crossings in the path-existence BFS
 *                (spec §8-8: a wall fully enclosing the ninja is legal while
 *                a jump remains, illegal once it is spent).
 *                removeWall / diagonalStep intentionally have NO pathEffect:
 *                counting them in the reachability guarantee would make wall
 *                legality diverge (removal restores a hand wall, diagonal
 *                corner-slips explode the search), so path legality sticks
 *                to the base rule for them.
 *   generate(state, playerIndex, ctx) -> Move[]   legal ability moves.
 *                ctx = { boardSize, wallSet, occupied, gimmickMap,
 *                        gimmickMapFor, slideFor, reachesGoal } — see
 *                abilityContext() in index.js. The first four are the actor's
 *                view (gimmickMap already null if the actor ignores gimmicks);
 *                the closures let an ability reason about ANOTHER player's
 *                gimmick immunity / path, which shove & swap need because they
 *                relocate the opponent, not the actor:
 *                  gimmickMapFor(i)         -> player i's gimmick map (or null)
 *                  slideFor(i, cell, dc, dr)-> resolve an ice slide for player i
 *                  reachesGoal(i, cell)     -> does player i still reach their
 *                                              goal row from `cell`?
 *   apply(nextState, playerIndex, move, ctx) -> void   mutates the already-
 *                cloned next state. `ctx` is abilityContext(next, playerIndex)
 *                so abilities that relocate the opponent (shove) can recompute
 *                the destination deterministically WITHOUT trusting extra
 *                fields on the (structurally matched) move. usesLeft
 *                bookkeeping — and the shared landing pipeline (win check +
 *                swamp for EVERY relocated pawn) — are done by the engine.
 */

import { stepBlockedByWall } from './walls.js';
import { resolveSlide, cellKey } from './gimmicks.js';
import { bfsDistanceToRow } from './path.js';

const ORTHO = [
  { dc: 0, dr: 1 },
  { dc: 0, dr: -1 },
  { dc: 1, dr: 0 },
  { dc: -1, dr: 0 },
];

const DIAG = [
  { dc: 1, dr: 1 },
  { dc: 1, dr: -1 },
  { dc: -1, dr: 1 },
  { dc: -1, dr: -1 },
];

function onBoard(boardSize, col, row) {
  return col >= 0 && row >= 0 && col < boardSize && row < boardSize;
}

/**
 * Self-strand guard: a movement ability that would leave the mover with NO
 * remaining route to their goal row is illegal. Walled-off pockets can
 * legally exist on the board (wall legality only guarantees paths from the
 * pawns' current cells), so without this guard a ninja could jump — or an
 * explorer corner-squeeze — into a sealed pocket and the engine invariant
 * "every legally reached state has a path for both players" would break.
 * This mirrors the wall-placement path guarantee, using the mover's
 * POST-move context (e.g. the jump just spent no longer counts).
 */
/**
 * Pre-slide landing cell of a shove: the opponent at `foe` is pushed in the
 * unit orthogonal direction (dc, dr) by UP TO two cells. Stop conditions are
 * evaluated one cell at a time, in this fixed order:
 *   1. First push cell — off-board / occupied / walled off from `foe` => the
 *      shove is impossible (return null; generate rejects it).
 *   2. Second push cell — same three checks against the FIRST cell; any failure
 *      leaves the pawn on the first cell (a one-cell push). Only when all three
 *      pass does the pawn advance the full two cells.
 * Ice sliding is applied by the caller from the returned cell — this helper
 * resolves only the deterministic 1-or-2 orthogonal push distance. Shared by
 * generate() and apply() so the recomputed destination is identical.
 */
function shovePushCell(ctx, foe, dc, dr) {
  const first = { col: foe.col + dc, row: foe.row + dr };
  if (!onBoard(ctx.boardSize, first.col, first.row)) return null;
  if (ctx.occupied.has(cellKey(first.col, first.row))) return null;
  if (stepBlockedByWall(ctx.wallSet, foe, first)) return null;
  // First cell is clear: try to extend to a second cell (max push = 2).
  const second = { col: first.col + dc, row: first.row + dr };
  if (
    onBoard(ctx.boardSize, second.col, second.row) &&
    !ctx.occupied.has(cellKey(second.col, second.row)) &&
    !stepBlockedByWall(ctx.wallSet, first, second)
  ) {
    return second;
  }
  return first;
}

function keepsOwnPath(state, playerIndex, cell, wallJumpsAfter, ctx) {
  return (
    bfsDistanceToRow(ctx.boardSize, ctx.wallSet, cell, state.players[playerIndex].goalRow, {
      wallJumps: wallJumpsAfter,
      gimmickMap: ctx.gimmickMap,
    }) >= 0
  );
}

export const ABILITIES = {
  /**
   * wallJump — hop over exactly one wall segment to the directly adjacent
   * cell behind it. The landing cell must be on the board and empty. The
   * jump IS the whole turn (one move). Ice sliding applies to the landing.
   * `to` in the Move is the FINAL resting cell (after any slide), matching
   * the convention of regular moves so the UI can highlight destinations.
   */
  wallJump: {
    id: 'wallJump',
    movement: true,
    pathEffect: 'wallCrossing',
    generate(state, playerIndex, ctx) {
      const me = state.players[playerIndex].pawn;
      const moves = [];
      const seen = new Set();
      for (const { dc, dr } of ORTHO) {
        const col = me.col + dc;
        const row = me.row + dr;
        if (!onBoard(ctx.boardSize, col, row)) continue;
        // Must actually cross a wall segment (adjacent cells are separated
        // by at most one wall thickness, so this is always "one wall").
        if (!stepBlockedByWall(ctx.wallSet, me, { col, row })) continue;
        if (ctx.occupied.has(cellKey(col, row))) continue; // landing must be empty
        const final = resolveSlide(
          ctx.boardSize,
          ctx.wallSet,
          ctx.gimmickMap,
          ctx.occupied,
          { col, row },
          dc,
          dr
        );
        // ゴール行は通常移動でのみ入れる — 壁バイパス能力の終盤決定力を抑える統一ルール
        if (final.row === state.players[playerIndex].goalRow) continue;
        // Post-jump budget: this use is spent.
        const jumpsAfter = state.players[playerIndex].ability.usesLeft - 1;
        if (!keepsOwnPath(state, playerIndex, final, jumpsAfter, ctx)) continue;
        const key = cellKey(final.col, final.row);
        if (!seen.has(key)) {
          seen.add(key);
          moves.push({ type: 'ability', ability: 'wallJump', to: final });
        }
      }
      return moves;
    },
    apply(next, playerIndex, move) {
      next.players[playerIndex].pawn = { col: move.to.col, row: move.to.row };
    },
  },

  /**
   * removeWall — remove one placed wall. The wall is destroyed, not
   * returned: it does NOT go back to its owner's hand (owner.wallsLeft is
   * left untouched). Costs the turn. Not a movement ability, so it stays
   * legal while swamped.
   */
  removeWall: {
    id: 'removeWall',
    movement: false,
    pathEffect: null,
    generate(state) {
      return state.walls.map((w) => ({
        type: 'ability',
        ability: 'removeWall',
        at: { col: w.col, row: w.row },
        orientation: w.orientation,
      }));
    },
    apply(next, playerIndex, move) {
      const i = next.walls.findIndex(
        (w) =>
          w.col === move.at.col &&
          w.row === move.at.row &&
          w.orientation === move.orientation
      );
      next.walls.splice(i, 1);
    },
  },

  /**
   * diagonalStep — move one cell diagonally. The target must be on the
   * board and empty. Walls never block it (wall geometry only defines
   * orthogonal edges, so corner-squeezing past touching walls is allowed —
   * the permissive reading, chosen for implementation simplicity).
   * Diagonal entry does not trigger ice sliding (see gimmicks.js); the
   * canonical user (explorer) ignores gimmicks anyway.
   */
  diagonalStep: {
    id: 'diagonalStep',
    movement: true,
    pathEffect: null,
    generate(state, playerIndex, ctx) {
      const me = state.players[playerIndex].pawn;
      const moves = [];
      for (const { dc, dr } of DIAG) {
        const col = me.col + dc;
        const row = me.row + dr;
        if (!onBoard(ctx.boardSize, col, row)) continue;
        if (ctx.occupied.has(cellKey(col, row))) continue;
        // ゴール行は通常移動でのみ入れる — 壁バイパス能力の終盤決定力を抑える統一ルール
        if (row === state.players[playerIndex].goalRow) continue;
        // Self-strand guard (see keepsOwnPath): the corner-squeeze must not
        // drop the mover into a sealed pocket. diagonalStep grants no wall
        // crossings, so the post-move jump budget is 0.
        if (!keepsOwnPath(state, playerIndex, { col, row }, 0, ctx)) continue;
        moves.push({ type: 'ability', ability: 'diagonalStep', to: { col, row } });
      }
      return moves;
    },
    apply(next, playerIndex, move) {
      next.players[playerIndex].pawn = { col: move.to.col, row: move.to.row };
    },
  },

  /**
   * dash (sprinter) — a straight two-cell rush in one orthogonal direction.
   * Both the first and the second step must be wall-free, and both the
   * middle and the landing cell must be empty (no face-to-face jump combo).
   * The landing goes through ice sliding in the travel direction (spec:
   * entry direction = movement direction). `to` is the FINAL resting cell.
   * Movement ability, so it is blocked while swamped; guarded by the
   * self-strand rule (the rush must not drop the sprinter into a sealed
   * pocket — dash grants no wall crossings, so the actor's normal path
   * context already reflects the post-move budget).
   */
  dash: {
    id: 'dash',
    movement: true,
    pathEffect: null,
    generate(state, playerIndex, ctx) {
      const me = state.players[playerIndex].pawn;
      const moves = [];
      const seen = new Set();
      for (const { dc, dr } of ORTHO) {
        const mid = { col: me.col + dc, row: me.row + dr };
        const land = { col: me.col + 2 * dc, row: me.row + 2 * dr };
        if (!onBoard(ctx.boardSize, land.col, land.row)) continue;
        // Both steps unobstructed and both cells empty.
        if (stepBlockedByWall(ctx.wallSet, me, mid)) continue;
        if (stepBlockedByWall(ctx.wallSet, mid, land)) continue;
        if (ctx.occupied.has(cellKey(mid.col, mid.row))) continue;
        if (ctx.occupied.has(cellKey(land.col, land.row))) continue;
        const final = ctx.slideFor(playerIndex, land, dc, dr);
        // ゴール行は通常移動でのみ入れる — 壁バイパス能力の終盤決定力を抑える統一ルール
        if (final.row === state.players[playerIndex].goalRow) continue;
        if (!ctx.reachesGoal(playerIndex, final)) continue; // self-strand guard
        const key = cellKey(final.col, final.row);
        if (seen.has(key)) continue;
        seen.add(key);
        moves.push({ type: 'ability', ability: 'dash', to: final });
      }
      return moves;
    },
    apply(next, playerIndex, move) {
      next.players[playerIndex].pawn = { col: move.to.col, row: move.to.row };
    },
  },

  /**
   * swap (phantom) — exchange the phantom's pawn with the opponent's,
   * regardless of distance. It is a teleport, so neither pawn slides on ice
   * (no entry direction — same rule as diagonalStep). BOTH pawns must retain
   * a route to their OWN goal after the swap (engine invariant); the
   * opponent is checked with the opponent's own path context (their jumps,
   * their gimmick immunity). `to` is the opponent's current cell (the UI
   * target). Movement ability → blocked while swamped.
   *
   * Landing on one's own goal row via swap is NOT a legal move: a swap that
   * would drop the phantom directly onto its goal row is excluded from
   * generate(). This closes a rule hole where, on turn 1, the opponent still
   * stands on their own starting cell — which is the PHANTOM's goal row —
   * so an opening swap was an instant win. Winning must be earned by
   * walking onto the goal row under the normal move rules.
   */
  swap: {
    id: 'swap',
    movement: true,
    pathEffect: null,
    generate(state, playerIndex, ctx) {
      const opp = 1 - playerIndex;
      const me = state.players[playerIndex].pawn;
      const foe = state.players[opp].pawn;
      // ゴール行は通常移動でのみ入れる — 壁バイパス能力の終盤決定力を抑える統一ルール
      if (foe.row === state.players[playerIndex].goalRow) return []; // no swap-to-win
      if (!ctx.reachesGoal(playerIndex, foe)) return []; // phantom would strand
      if (!ctx.reachesGoal(opp, me)) return []; // opponent would strand
      return [{ type: 'ability', ability: 'swap', to: { col: foe.col, row: foe.row } }];
    },
    apply(next, playerIndex, move) {
      const opp = 1 - playerIndex;
      const me = next.players[playerIndex].pawn;
      const foe = next.players[opp].pawn;
      next.players[playerIndex].pawn = { col: foe.col, row: foe.row };
      next.players[opp].pawn = { col: me.col, row: me.row };
    },
  },

  /**
   * shove (brute) — push an orthogonally-adjacent opponent straight away from
   * the brute, UP TO two cells. The first push cell must be on the board,
   * empty, and not walled off from the opponent's cell (a wall between the
   * brute and the opponent does NOT block the shove — the brute reaches
   * through, like the thief). When that first cell is clear the push extends
   * to a SECOND cell as long as it too is on the board, empty, and not walled
   * off from the first cell; any of those failing stops the push at one cell
   * (see shovePushCell for the exact ordering / stop conditions). Ice sliding
   * is then applied from the final push cell (its OWNER's gimmick immunity
   * applies, not the brute's), and the opponent must retain a route to its own
   * goal. The brute itself never moves, so this is a non-movement ability and
   * stays usable while swamped (spec: enables the hex+shove synergy). `to` is
   * the opponent's current cell (UI target); the destination is recomputed in
   * apply so no forged field is ever trusted.
   */
  shove: {
    id: 'shove',
    movement: false,
    pathEffect: null,
    generate(state, playerIndex, ctx) {
      const opp = 1 - playerIndex;
      const me = state.players[playerIndex].pawn;
      const foe = state.players[opp].pawn;
      const dc = foe.col - me.col;
      const dr = foe.row - me.row;
      if (Math.abs(dc) + Math.abs(dr) !== 1) return []; // not ortho-adjacent
      const push = shovePushCell(ctx, foe, dc, dr);
      if (!push) return []; // first push cell off-board / occupied / walled
      const final = ctx.slideFor(opp, push, dc, dr);
      if (!ctx.reachesGoal(opp, final)) return []; // opponent would strand
      return [{ type: 'ability', ability: 'shove', to: { col: foe.col, row: foe.row } }];
    },
    apply(next, playerIndex, move, ctx) {
      const opp = 1 - playerIndex;
      const me = next.players[playerIndex].pawn;
      const foe = next.players[opp].pawn;
      const dc = foe.col - me.col;
      const dr = foe.row - me.row;
      const push = shovePushCell(ctx, foe, dc, dr);
      const final = ctx.slideFor(opp, push, dc, dr);
      next.players[opp].pawn = { col: final.col, row: final.row };
      // Swamp on the landing cell is applied by the engine's landing loop.
    },
  },

  /**
   * hex (hexer) — curse the opponent so its next own turn is SKIPPED entirely:
   * a hexed player has no legal move but `pass` (no pawn move, no wall, no
   * ability), and serving that pass clears the curse (engine legalMoves /
   * applyMove handle players[i].effects.hexed, symmetric to swamped). This is a
   * strictly stronger effect than a swamp tile's movement-only lock — a whole
   * lost tempo — chosen so "spend a turn to steal a turn" is tempo-positive.
   * Distance-unlimited; illegal to generate while the opponent is already
   * hexed (no double-stacking). Unlike a swamp TILE, the hex is a curse rather
   * than a gimmick, so explorer's ignoreGimmicks does NOT protect against it
   * (the spec exempts explorer only from swamp-tile landings). Non-movement
   * ability → usable while the hexer itself is swamped. `to` is the opponent's
   * cell (UI target).
   */
  hex: {
    id: 'hex',
    movement: false,
    pathEffect: null,
    generate(state, playerIndex) {
      const opp = 1 - playerIndex;
      const foe = state.players[opp];
      if (foe.effects && foe.effects.hexed) return []; // already hexed (no stacking)
      return [{ type: 'ability', ability: 'hex', to: { col: foe.pawn.col, row: foe.pawn.row } }];
    },
    apply(next, playerIndex) {
      const opp = 1 - playerIndex;
      next.players[opp].effects = { ...next.players[opp].effects, hexed: true };
    },
  },

  /**
   * steal (thief) — take one wall from an orthogonally-adjacent opponent
   * (opponent.wallsLeft -= 1, thief.wallsLeft += 1). Adjacency ignores walls
   * (the thief reaches over them). Illegal to generate if the opponent has no
   * walls left. Non-movement ability → usable while swamped. `to` is the
   * opponent's cell (UI target). No path guard is needed: transferring a
   * hand wall changes no placed wall, so connectivity is untouched.
   */
  steal: {
    id: 'steal',
    movement: false,
    pathEffect: null,
    generate(state, playerIndex) {
      const opp = 1 - playerIndex;
      if (state.players[opp].wallsLeft <= 0) return []; // nothing to steal
      const me = state.players[playerIndex].pawn;
      const foe = state.players[opp].pawn;
      if (Math.abs(foe.col - me.col) + Math.abs(foe.row - me.row) !== 1) return [];
      return [{ type: 'ability', ability: 'steal', to: { col: foe.col, row: foe.row } }];
    },
    apply(next, playerIndex) {
      const opp = 1 - playerIndex;
      next.players[opp].wallsLeft -= 1;
      next.players[playerIndex].wallsLeft += 1;
    },
  },
};
